<template>
  <div class="popup-config">
    <div class="editor-heading">
      <div>
        <p class="eyebrow">Popups / Annonces</p>
        <h2>Annonce publique</h2>
        <p>Configurez un message simple qui sera affiché sur les pages publiques selon la langue et les dates choisies.</p>
      </div>
      <label class="switch">
        <input v-model="localPopup.enabled" type="checkbox" />
        <span>{{ localPopup.enabled ? 'Actif' : 'Inactif' }}</span>
      </label>
    </div>

    <p v-if="successMessage" class="message success">{{ successMessage }}</p>
    <p v-if="errorMessage" class="message error">{{ errorMessage }}</p>
    <p v-if="englishFallbackWarning" class="message warning">
      Certains champs anglais sont vides. Le site anglais utilisera le contenu français en secours.
    </p>

    <div class="editor-grid">
      <section class="panel">
        <h3>Paramètres</h3>
        <div class="form-row">
          <label>
            Template
            <select v-model="localPopup.template" @change="applyTemplate">
              <option v-for="template in templates" :key="template.value" :value="template.value">
                {{ template.label }}
              </option>
            </select>
          </label>
          <label>
            Mode d’affichage
            <select v-model="localPopup.display_mode">
              <option value="modal">Modal classique</option>
              <option value="fullscreen">Plein écran</option>
            </select>
          </label>
        </div>

        <div class="form-row">
          <label>
            Langue
            <select v-model="localPopup.language">
              <option value="both">Français + Anglais</option>
              <option value="fr">Français seulement</option>
              <option value="en">Anglais seulement</option>
            </select>
          </label>
        </div>

        <div class="form-row">
          <label>
            Date de début optionnelle
            <input v-model="localPopup.start_at" type="date" />
          </label>
          <label>
            Date de fin optionnelle
            <input v-model="localPopup.end_at" type="date" />
          </label>
        </div>
      </section>

      <section class="panel">
        <h3>Couleurs et image</h3>
        <div class="color-grid">
          <label>
            Fond
            <input v-model="localPopup.background_color" type="color" />
          </label>
          <label>
            Texte
            <input v-model="localPopup.text_color" type="color" />
          </label>
          <label>
            Accent
            <input v-model="localPopup.accent_color" type="color" />
          </label>
          <label>
            Fond bouton
            <input v-model="localPopup.button_background_color" type="color" />
          </label>
          <label>
            Texte bouton
            <input v-model="localPopup.button_text_color" type="color" />
          </label>
        </div>
        <label>
          Image optionnelle
          <input v-model.trim="localPopup.image_url" type="url" placeholder="https://exemple.com/image.jpg" />
          <small>Collez une URL d’image déjà en ligne ou disponible dans les assets publics. Aucun upload n’est ajouté pour cette phase.</small>
        </label>
      </section>

      <section v-if="localPopup.template === 'closure'" class="panel">
        <h3>Fermeture temporaire</h3>
        <div class="form-row">
          <label>
            Date de fermeture
            <input v-model="templateFields.closure_date" type="date" @input="applyTemplate" />
          </label>
          <label>
            Événement FR
            <input v-model="templateFields.event_fr" type="text" @input="applyTemplate" placeholder="la Fête du Canada" />
          </label>
        </div>
        <div class="form-row">
          <label>
            Event EN
            <input v-model="templateFields.event_en" type="text" @input="applyTemplate" placeholder="Canada Day" />
          </label>
          <label>
            Date de réouverture
            <input v-model="templateFields.reopen_date" type="date" @input="applyTemplate" />
          </label>
        </div>
        <label>
          Message complémentaire optionnel
          <textarea v-model="templateFields.extra_message" @input="applyTemplate" placeholder="Ex. Merci de votre compréhension."></textarea>
        </label>
      </section>

      <section v-if="localPopup.template === 'schedule'" class="panel">
        <h3>Changement d’horaire</h3>
        <div class="form-row">
          <label>
            Période ou date FR
            <input v-model="templateFields.schedule_period_fr" type="text" @input="applyTemplate" placeholder="le 24 décembre" />
          </label>
          <label>
            Period or date EN
            <input v-model="templateFields.schedule_period_en" type="text" @input="applyTemplate" placeholder="on December 24" />
          </label>
        </div>
        <div class="form-row">
          <label>
            Horaire FR
            <input v-model="templateFields.schedule_hours_fr" type="text" @input="applyTemplate" placeholder="de 9 h à 14 h" />
          </label>
          <label>
            Hours EN
            <input v-model="templateFields.schedule_hours_en" type="text" @input="applyTemplate" placeholder="from 9 AM to 2 PM" />
          </label>
        </div>
      </section>

      <section class="panel">
        <h3>Contenu</h3>
        <div class="form-row">
          <label>
            Titre FR
            <input v-model="localPopup.title_fr" type="text" />
          </label>
          <label>
            Title EN
            <input v-model="localPopup.title_en" type="text" />
          </label>
        </div>
        <div class="form-row">
          <label>
            Message FR
            <textarea v-model="localPopup.message_fr"></textarea>
          </label>
          <label>
            Message EN
            <textarea v-model="localPopup.message_en"></textarea>
          </label>
        </div>
      </section>

      <section class="panel">
        <h3>Bouton optionnel</h3>
        <div class="form-row">
          <label>
            Texte bouton FR
            <input v-model="localPopup.button_text_fr" type="text" placeholder="Voir les taux" />
          </label>
          <label>
            Button text EN
            <input v-model="localPopup.button_text_en" type="text" placeholder="View rates" />
          </label>
        </div>
        <label>
          Lien du bouton
          <input v-model="localPopup.button_url" type="text" placeholder="/taux-de-change" />
        </label>
      </section>
    </div>

    <section class="preview-panel">
      <div class="preview-heading">
        <h3>Aperçu</h3>
        <div class="preview-tabs">
          <button type="button" :class="{ active: previewLanguage === 'fr' }" @click="previewLanguage = 'fr'">FR</button>
          <button type="button" :class="{ active: previewLanguage === 'en' }" @click="previewLanguage = 'en'">EN</button>
        </div>
      </div>
      <div
        class="popup-preview"
        :class="[localPopup.template, previewModeClass]"
        :style="previewStyle"
      >
        <button type="button" class="preview-close">×</button>
        <img v-if="localPopup.image_url" class="preview-image" :src="localPopup.image_url" alt="" />
        <p class="preview-label">{{ templateLabel }}</p>
        <h4>{{ previewContent.title || 'Titre du popup' }}</h4>
        <p>{{ previewContent.message || 'Message du popup...' }}</p>
        <a
          v-if="previewContent.buttonText && localPopup.button_url"
          href="#"
          :style="previewButtonStyle"
          @click.prevent
        >{{ previewContent.buttonText }}</a>
      </div>
    </section>

    <div class="actions">
      <button type="button" class="btn-secondary" @click="resetLocal">Réinitialiser</button>
      <button type="button" class="btn-primary" :disabled="saving" @click="saveSettings">
        {{ saving ? 'Enregistrement...' : 'Enregistrer le popup' }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { apiUrl } from '@/config/api';

const defaultPopup = () => ({
  type: 'popups',
  enabled: false,
  template: 'general',
  language: 'both',
  title_fr: 'Information Nyenyezia Forex',
  title_en: 'Nyenyezia Forex Update',
  message_fr: 'Un message important sera affiché ici.',
  message_en: 'An important update will be displayed here.',
  start_at: '',
  end_at: '',
  button_text_fr: '',
  button_text_en: '',
  button_url: '',
  display_mode: 'modal',
  background_color: '#ffffff',
  text_color: '#172033',
  accent_color: '#c39446',
  button_background_color: '#007acc',
  button_text_color: '#ffffff',
  image_url: ''
});

const defaultTemplateFields = () => ({
  closure_date: '2026-07-01',
  event_fr: 'la Fête du Canada',
  event_en: 'Canada Day',
  reopen_date: '2026-07-02',
  extra_message: '',
  schedule_period_fr: 'le jour indiqué',
  schedule_period_en: 'on the selected day',
  schedule_hours_fr: 'selon un horaire spécial',
  schedule_hours_en: 'on a special schedule'
});

export default {
  name: 'PopupsConfig',
  props: {
    fieldData: {
      type: Object,
      default: () => ({})
    },
    fieldKey: String,
    token: String,
    setLoading: Function
  },
  data() {
    return {
      localPopup: defaultPopup(),
      templateFields: defaultTemplateFields(),
      previewLanguage: 'fr',
      saving: false,
      successMessage: '',
      errorMessage: '',
      templates: [
        { value: 'closure', label: 'Fermeture temporaire' },
        { value: 'promotion', label: 'Promotion / information spéciale' },
        { value: 'schedule', label: 'Changement d’horaire' },
        { value: 'general', label: 'Message général' }
      ]
    };
  },
  computed: {
    englishFallbackWarning() {
      return ['both', 'en'].includes(this.localPopup.language)
        && (!this.localPopup.title_en || !this.localPopup.message_en);
    },
    templateLabel() {
      const found = this.templates.find((template) => template.value === this.localPopup.template);
      return found ? found.label : 'Message';
    },
    previewContent() {
      const lang = this.previewLanguage;
      const title = lang === 'en'
        ? (this.localPopup.title_en || this.localPopup.title_fr)
        : this.localPopup.title_fr;
      const message = lang === 'en'
        ? (this.localPopup.message_en || this.localPopup.message_fr)
        : this.localPopup.message_fr;
      const buttonText = lang === 'en'
        ? (this.localPopup.button_text_en || this.localPopup.button_text_fr)
        : this.localPopup.button_text_fr;

      return { title, message, buttonText };
    },
    colorDefaults() {
      if (this.localPopup.display_mode === 'fullscreen') {
        return {
          background_color: '#001041',
          text_color: '#ffffff',
          accent_color: '#c39446',
          button_background_color: '#c39446',
          button_text_color: '#001041'
        };
      }

      return {
        background_color: '#ffffff',
        text_color: '#172033',
        accent_color: '#c39446',
        button_background_color: '#007acc',
        button_text_color: '#ffffff'
      };
    },
    previewModeClass() {
      return this.localPopup.display_mode === 'fullscreen' ? 'preview-fullscreen' : 'preview-modal';
    },
    safeColors() {
      return {
        background: this.localPopup.background_color || this.colorDefaults.background_color,
        text: this.localPopup.text_color || this.colorDefaults.text_color,
        accent: this.localPopup.accent_color || this.colorDefaults.accent_color,
        buttonBackground: this.localPopup.button_background_color || this.colorDefaults.button_background_color,
        buttonText: this.localPopup.button_text_color || this.colorDefaults.button_text_color
      };
    },
    previewStyle() {
      return {
        backgroundColor: this.safeColors.background,
        color: this.safeColors.text,
        borderColor: this.safeColors.accent,
        borderLeftColor: this.safeColors.accent
      };
    },
    previewButtonStyle() {
      return {
        backgroundColor: this.safeColors.buttonBackground,
        color: this.safeColors.buttonText
      };
    }
  },
  watch: {
    fieldData: {
      handler() {
        this.resetLocal();
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    resetLocal() {
      this.localPopup = {
        ...defaultPopup(),
        ...(this.fieldData || {}),
        type: 'popups'
      };
      if (!this.localPopup.template) this.localPopup.template = 'general';
      if (!this.localPopup.language) this.localPopup.language = 'both';
      if (!['modal', 'fullscreen'].includes(this.localPopup.display_mode)) {
        this.localPopup.display_mode = 'modal';
      }
      this.successMessage = '';
      this.errorMessage = '';
    },
    formatDateFr(value) {
      if (!value) return '[date]';
      const date = new Date(value + 'T00:00:00');
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString('fr-CA', { day: 'numeric', month: 'long' });
    },
    formatDateEn(value) {
      if (!value) return '[date]';
      const date = new Date(value + 'T00:00:00');
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString('en-CA', { month: 'long', day: 'numeric' });
    },
    applyTemplate() {
      const fields = this.templateFields;

      if (this.localPopup.template === 'closure') {
        this.localPopup.title_fr = 'Fermeture temporaire';
        this.localPopup.title_en = 'Temporary closure';
        const reopenFr = fields.reopen_date ? ` Nous reprendrons nos services le ${this.formatDateFr(fields.reopen_date)}.` : '';
        const reopenEn = fields.reopen_date ? ` Services will resume on ${this.formatDateEn(fields.reopen_date)}.` : '';
        const extra = fields.extra_message ? ` ${fields.extra_message}` : '';
        this.localPopup.message_fr = `Nyenyezia Forex sera fermé le ${this.formatDateFr(fields.closure_date)} pour ${fields.event_fr || '[événement]'}.${reopenFr}${extra}`;
        this.localPopup.message_en = `Nyenyezia Forex will be closed on ${this.formatDateEn(fields.closure_date)} for ${fields.event_en || '[event]'}.${reopenEn}`;
        return;
      }

      if (this.localPopup.template === 'promotion') {
        this.localPopup.title_fr = 'Information spéciale';
        this.localPopup.title_en = 'Special update';
        this.localPopup.message_fr = 'Consultez cette annonce temporaire avant votre prochaine opération.';
        this.localPopup.message_en = 'Please review this temporary update before your next transaction.';
        return;
      }

      if (this.localPopup.template === 'schedule') {
        this.localPopup.title_fr = 'Changement d’horaire';
        this.localPopup.title_en = 'Schedule change';
        this.localPopup.message_fr = `Nyenyezia Forex sera ouvert ${fields.schedule_period_fr || 'le jour indiqué'} ${fields.schedule_hours_fr || 'selon un horaire spécial'}.`;
        this.localPopup.message_en = `Nyenyezia Forex will be open ${fields.schedule_period_en || 'on the selected day'} ${fields.schedule_hours_en || 'on a special schedule'}.`;
        return;
      }

      this.localPopup.title_fr = this.localPopup.title_fr || 'Information Nyenyezia Forex';
      this.localPopup.title_en = this.localPopup.title_en || 'Nyenyezia Forex Update';
      this.localPopup.message_fr = this.localPopup.message_fr || 'Ajoutez votre message général ici.';
      this.localPopup.message_en = this.localPopup.message_en || 'Add your general message here.';
    },
    normalizedPopup() {
      return {
        ...defaultPopup(),
        ...this.localPopup,
        type: 'popups',
        enabled: Boolean(this.localPopup.enabled),
        display_mode: ['modal', 'fullscreen'].includes(this.localPopup.display_mode) ? this.localPopup.display_mode : 'modal',
        background_color: this.localPopup.background_color || this.colorDefaults.background_color,
        text_color: this.localPopup.text_color || this.colorDefaults.text_color,
        accent_color: this.localPopup.accent_color || this.colorDefaults.accent_color,
        button_background_color: this.localPopup.button_background_color || this.colorDefaults.button_background_color,
        button_text_color: this.localPopup.button_text_color || this.colorDefaults.button_text_color,
        image_url: this.localPopup.image_url || ''
      };
    },
    colorContrast(hexA, hexB) {
      const parse = (hex) => {
        const value = String(hex || '').replace('#', '');
        if (!/^[0-9a-f]{6}$/i.test(value)) return null;
        const rgb = [0, 2, 4].map((start) => parseInt(value.slice(start, start + 2), 16) / 255);
        const linear = rgb.map((channel) => (channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4)));
        return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
      };
      const a = parse(hexA);
      const b = parse(hexB);
      if (a === null || b === null) return 21;
      const lighter = Math.max(a, b);
      const darker = Math.min(a, b);
      return (lighter + 0.05) / (darker + 0.05);
    },
    validatePopup() {
      if (this.localPopup.enabled && !this.localPopup.message_fr && !this.localPopup.message_en) {
        return 'Ajoutez au moins un message avant d’activer le popup.';
      }
      if (this.localPopup.start_at && this.localPopup.end_at && this.localPopup.start_at > this.localPopup.end_at) {
        return 'La date de fin doit être après la date de début.';
      }
      if (this.colorContrast(this.safeColors.background, this.safeColors.text) < 3) {
        return 'Le contraste entre le fond et le texte est trop faible.';
      }
      if (this.localPopup.button_url && this.colorContrast(this.safeColors.buttonBackground, this.safeColors.buttonText) < 3) {
        return 'Le contraste entre le bouton et son texte est trop faible.';
      }
      return '';
    },
    async saveSettings() {
      const validationError = this.validatePopup();
      if (validationError) {
        this.errorMessage = validationError;
        this.successMessage = '';
        return;
      }

      this.saving = true;
      this.errorMessage = '';
      this.successMessage = '';
      if (this.setLoading) this.setLoading(true);

      try {
        const config = this.normalizedPopup();
        await axios.put(apiUrl('/page'), {
          name: 'home',
          section: 'site_popup',
          config
        }, {
          headers: {
            Authorization: 'Bearer ' + this.token,
            'Content-Type': 'application/json'
          }
        });

        this.localPopup = config;
        this.successMessage = 'Popup enregistré avec succès.';
        this.$emit('update-field', {
          key: this.fieldKey || 'popups',
          fieldName: null,
          value: config
        });
        this.$emit('notice', {
          type: 'success',
          text: 'Popup enregistré avec succès.'
        });
      } catch (error) {
        console.error(error);
        this.errorMessage = 'Impossible d’enregistrer le popup. Vérifiez la connexion admin.';
        this.$emit('notice', {
          type: 'error',
          text: 'Impossible d’enregistrer le popup.'
        });
      } finally {
        this.saving = false;
        if (this.setLoading) this.setLoading(false);
      }
    }
  }
};
</script>

<style scoped>
.popup-config {
  color: #172033;
}

.editor-heading,
.panel,
.preview-panel {
  background: #fff;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(0, 16, 65, 0.06);
}

.editor-heading {
  align-items: center;
  display: flex;
  gap: 20px;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px;
}

.editor-heading h2,
.panel h3,
.preview-panel h3 {
  color: #001041;
  margin: 0;
}

.editor-heading p {
  color: #53647c;
  margin: 6px 0 0;
}

.eyebrow {
  color: #007acc;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0;
  margin: 0 0 6px;
  text-transform: uppercase;
}

.switch {
  align-items: center;
  background: #f4f7fb;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  white-space: nowrap;
}

.switch input {
  height: 18px;
  width: 18px;
}

.editor-grid {
  display: grid;
  gap: 18px;
}

.panel,
.preview-panel {
  padding: 20px;
}

.panel h3 {
  margin-bottom: 16px;
}

.form-row {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.color-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin-bottom: 14px;
}

label {
  color: #2d405d;
  display: block;
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 14px;
}

input,
select,
textarea {
  background: #fbfdff;
  border: 1px solid #cfd9e8;
  border-radius: 8px;
  box-sizing: border-box;
  color: #172033;
  display: block;
  font-size: 0.98rem;
  margin-top: 7px;
  outline: none;
  padding: 12px 13px;
  width: 100%;
}

input[type="color"] {
  cursor: pointer;
  height: 44px;
  padding: 5px;
}

label small {
  color: #66758c;
  display: block;
  font-weight: 400;
  line-height: 1.45;
  margin-top: 6px;
}

textarea {
  min-height: 104px;
  resize: vertical;
}

input:focus,
select:focus,
textarea:focus {
  border-color: #007acc;
  box-shadow: 0 0 0 3px rgba(0, 122, 204, 0.14);
}

.message {
  border-radius: 8px;
  margin: 0 0 14px;
  padding: 12px 14px;
}

.message.success {
  background: #eefbf3;
  border: 1px solid #b9e5c7;
  color: #166534;
}

.message.error {
  background: #fff3f2;
  border: 1px solid #f0c3bd;
  color: #8b1e1e;
}

.message.warning {
  background: #fff8e5;
  border: 1px solid #f0d78a;
  color: #725400;
}

.preview-panel {
  margin-top: 18px;
}

.preview-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
}

.preview-tabs {
  display: flex;
  gap: 8px;
}

.preview-tabs button {
  background: #eef3fb;
  border: 1px solid #cfd9e8;
  border-radius: 8px;
  color: #001041;
  cursor: pointer;
  padding: 8px 12px;
}

.preview-tabs button.active {
  background: #001041;
  color: #fff;
}

.popup-preview {
  background: #fff;
  border: 1px solid #dbe3ef;
  border-left: 5px solid #007acc;
  border-radius: 8px;
  box-shadow: 0 14px 34px rgba(0, 16, 65, 0.12);
  max-width: 520px;
  padding: 20px;
  position: relative;
}

.popup-preview.preview-fullscreen {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 420px;
  max-width: none;
  text-align: center;
}

.popup-preview.preview-fullscreen h4,
.popup-preview.preview-fullscreen p {
  max-width: 680px;
}

.popup-preview.closure {
  border-left-color: #c39446;
}

.popup-preview.promotion {
  border-left-color: #166534;
}

.popup-preview.schedule {
  border-left-color: #7c3aed;
}

.preview-close {
  background: #eef3fb;
  border: none;
  border-radius: 50%;
  color: #001041;
  height: 30px;
  line-height: 1;
  position: absolute;
  right: 12px;
  top: 12px;
  width: 30px;
}

.preview-label {
  color: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  margin: 0 38px 8px 0;
  opacity: 0.78;
  text-transform: uppercase;
}

.popup-preview h4 {
  color: inherit;
  font-size: 1.25rem;
  margin: 0 38px 8px 0;
}

.popup-preview p {
  color: inherit;
  line-height: 1.6;
  margin: 0 0 14px;
}

.popup-preview a {
  background: #007acc;
  border-radius: 8px;
  color: #fff;
  display: inline-block;
  font-weight: 700;
  padding: 10px 14px;
  text-decoration: none;
}

.preview-image {
  border-radius: 8px;
  display: block;
  margin: 0 38px 16px 0;
  max-height: 220px;
  max-width: 100%;
  object-fit: cover;
  width: 100%;
}

.preview-fullscreen .preview-image {
  margin: 0 0 20px;
  max-height: 260px;
  max-width: 620px;
}

.actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 18px;
}

.btn-primary,
.btn-secondary {
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;
  min-height: 44px;
  padding: 0 16px;
}

.btn-primary {
  background: #001041;
  color: #fff;
}

.btn-primary:disabled {
  cursor: wait;
  opacity: 0.7;
}

.btn-secondary {
  background: #eef3fb;
  color: #001041;
}

@media (max-width: 780px) {
  .editor-heading,
  .preview-heading {
    align-items: flex-start;
    display: block;
  }

  .switch,
  .preview-tabs {
    margin-top: 14px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .color-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .actions {
    display: grid;
  }
}
</style>
